import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Input,
  Button,
} from "@mui/joy";
import { Paper } from "@mui/material";
import { FileDownload, PictureAsPdf } from "@mui/icons-material";
import { connect } from "react-redux";
import DataTable from "react-data-table-component";
import { GET_REFERRALS_API_URL } from "../../configs/url";
import apiCall, { flatten } from "../../helpers/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  datatable_style,
  downloadTablePdf,
  exportToCsv,
  LoadingComponent,
  NoDataComponent,
} from "../../helpers/datatable_helpers";
import { useTranslation } from "react-i18next";

const Referrals = (props) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [filteredRows, setFilteredRows] = useState([]);
  const { settings } = props;
  const { t } = useTranslation();

  useEffect(() => {
    if (!props?.user || !props?.user?.auth_token) {
      window.location.href = "/login";
    }
    document.title = "My Referrals | KPG Partner";
  }, [props?.user]);

  const dataColumns = [
    {
      data: "created_at",
      name: "created_at",
      title: t("referral_column_date"),
      searchable: true,
    },
    {
      data: "name",
      name: "name",
      title: t("referral_column_name"),
      searchable: true,
    },
    {
      data: "orders",
      name: "orders",
      title: t("referral_column_orders"),
      searchable: false,
    },
    {
      data: "bonus_achievement",
      name: "bonus_achievement",
      title: t("referral_column_bonus_achievement"),
      searchable: false,
    },
    {
      data: "recruit_status",
      name: "recruit_status",
      title: t("referral_column_recruit_status"),
      searchable: false,
    },
    {
      data: "inactive_reason",
      name: "inactive_reason",
      title: t("referral_column_inactive_reason"),
      searchable: false,
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    const data = {
      draw: 1,
      start: 0,
      length: 10,
      search: { value: search, regex: false },
      columns: dataColumns,
    };
    try {
      const res = await apiCall({
        method: "POST",
        url: GET_REFERRALS_API_URL + "?" + new URLSearchParams(flatten(data)),
      });
      const processedData = res.data.data.map((row) => ({
        // ...row,
        // Ensure all fields are strings for search functionality
        name: row.name || "",
        created_at: row.created_at || "",
        bonus_achievement: row.bonus_achievement || "0",
        recruit_status: row.recruit_status || "Unknown",
        inactive_reason: row.inactive_reason || "N/A",
        orders: row.orders || 0,
      }));
      setRows(processedData);
      setFilteredRows(processedData);
      setTotal(res.data.recordsFiltered);
    } catch (err) {
      console.error("Error fetching referrals:", err);
      setRows([]);
      setFilteredRows([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearch(searchValue);
    setPage(1);
  };

  useEffect(() => {
    if (props?.user?.auth_token) {
      fetchData();
    }
  }, [props?.user, search, page, perPage]);

  const columns = useMemo(
    () => [
      {
        name: t("referral_column_name"),
        selector: (row) => row.name,
        sortable: true,
        minWidth: "120px",
        grow: 1,
        cell: (row) => (
          <Typography
            level="body-sm"
            fontWeight="md"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: 1.2,
            }}
            title={row.name || "N/A"}
          >
            {row.name || "N/A"}
          </Typography>
        ),
      },
      {
        name: t("referral_column_date"),
        selector: (row) => row.created_at,
        sortable: true,
        minWidth: "140px",
        cell: (row) => (
          <Typography
            level="body-sm"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {row.created_at}
          </Typography>
        ),
      },
      {
        name: t("referral_column_bonus_achievement"),
        selector: (row) => row.bonus_achievement,
        sortable: true,
        minWidth: "120px",
        maxWidth: "160px",
        cell: (row) => (
          <Typography component="span" className="amount-text">
            {settings[5].value} {row.bonus_achievement}
          </Typography>
        ),
      },
      {
        name: t("referral_column_orders"),
        selector: (row) => row.orders,
        sortable: true,
        minWidth: "140px",
        cell: (row) => (
          <Typography
            level="body-sm"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {row.orders}
          </Typography>
        ),
      },
      {
        name: t("referral_column_recruit_status"),
        selector: (row) => row.recruit_status,
        sortable: true,
        minWidth: "90px",
        cell: (row) => (
          <Chip
            variant="soft"
            color={
              row.recruit_status === "Active"
                ? "success"
                : row.recruit_status === "Fired" ||
                  row.recruit_status === "Rejected"
                ? "danger"
                : row.recruit_status === "Inactive"
                ? "warning"
                : "neutral"
            }
            size="sm"
            sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
          >
            {row.recruit_status || "Unknown"}
          </Chip>
        ),
      },
      {
        name: t("referral_column_inactive_reason"),
        selector: (row) => row.inactive_reason,
        sortable: true,
        minWidth: "140px",
        cell: (row) => (
          <Typography
            level="body-sm"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {row.inactive_reason || "N/A"}
          </Typography>
        ),
      },
    ],
    []
  );

  if (!props?.user?.auth_token) {
    return (
      <div className="loading-container">
        <CircularProgress size="sm" />
      </div>
    );
  }

  return (
    <div className="transactions-container">
      <div className="header-container">
        <Typography
          level="h4"
          sx={{ flexShrink: 0, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("sidebar_referrals")}
        </Typography>
        <div className="controls-container">
          <Input
            size="sm"
            placeholder={t("common_search")}
            value={search}
            onChange={handleSearchChange}
            className="search-input"
          />
          <div className="export-buttons-container">
            <Button
              size="sm"
              variant="soft"
              onClick={() => exportToCsv(filteredRows, "referrals")}
              disabled={total === 0}
              startDecorator={<FileDownload sx={{ fontSize: "16px" }} />}
              className="export-btn csv-btn"
            >
              {t("common_csv")}
            </Button>
            <Button
              size="sm"
              variant="soft"
              onClick={() =>
                downloadTablePdf(dataColumns, filteredRows, "referrals")
              }
              disabled={total === 0}
              startDecorator={<PictureAsPdf sx={{ fontSize: "16px" }} />}
              className="export-btn pdf-btn"
            >
              {t("common_pdf")}
            </Button>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <Paper elevation={0} className="table-paper">
          <DataTable
            columns={columns}
            data={filteredRows}
            progressPending={loading}
            paginationTotalRows={total}
            paginationServer
            progressComponent={<LoadingComponent />}
            noDataComponent={<NoDataComponent />}
            pagination
            paginationPerPage={perPage}
            onChangeRowsPerPage={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            responsive
            striped
            highlightOnHover
            customStyles={datatable_style}
            paginationComponentOptions={{
              rowsPerPageText: t("common_rows_per_page"),
              rangeSeparatorText: t("common_dt_separator"),
              selectAllRowsItem: true,
              selectAllRowsItemText: t("common_all"),
            }}
            dense={false}
          />
        </Paper>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  settings: state.settings.settings,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Referrals);
